import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Footprints,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Plus,
  Check,
  Minus,
  RotateCcw,
  CircleAlert,
} from "lucide-react";
import { AddDiseaseHistoryDialog } from "@/components/consultation/AddDiseaseHistoryDialog";
import { EmptyState } from "@/components/consultation/EmptyState";
import {
  DismissibleChip,
  ChipContainer,
} from "@/components/consultation/shared/DismissibleChip";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Table, TableBody, TableRow, TableCell } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  usePatientDiseaseHistory,
  useInitializeAppointmentDiseaseDraft,
  useConfirmDiseaseHistory,
  useRejectDiseaseHistory,
  useDeactivateDiseaseHistory,
  useDeleteDiseaseHistory,
  useExcludeFromPrescription,
  useIncludeInPrescription,
  useUpdateDiseaseHistory,
  useRejectCustomDisease,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/authStore";
import type {
  PatientInfo,
  PatientDiseaseHistory,
  FamilyDiseaseHistory,
  PatientLifestyle,
  Activity,
  ActivityType,
  ActivityFrequency,
  ActivityDuration,
  LifestyleType,
  LifestyleFrequency,
  AppointmentHistory,
} from "@/types/appointments";

interface PatientOverviewProps {
  patientInfo?: PatientInfo;
  patientId: string;
  appointmentId: string;
  appointmentStartedAt?: string;
  familyHistory: FamilyDiseaseHistory[];
  patientLifestyle: PatientLifestyle[];
  patientActivity: Activity[];
  lifestyleTypes: LifestyleType[];
  lifestyleFrequencies: LifestyleFrequency[];
  activityTypes: ActivityType[];
  activityFrequencies: ActivityFrequency[];
  activityDurations: ActivityDuration[];
  lastVisits?: AppointmentHistory[];
  onAddConfirmed?: () => void;
  onManageFatherHistory: () => void;
  onManageMotherHistory: () => void;
  onManageSiblingHistory: () => void;
  onRemoveFamilyDiseaseHistory?: (id: number | string) => void;
  onReAddFamilyDiseaseHistory?: (payload: Record<string, unknown>) => void;
  onManageLifestyle: () => void;
  onManageActivity: () => void;
  onOpenDocument?: (doc: any) => void;
  isCompleted?: boolean;
}

const isPastDraft = (row: PatientDiseaseHistory) =>
  row.draft_context === "past";

/* ------------------------------------------------------------------ */
/*  HistoryInfoDialog                                                  */
/* ------------------------------------------------------------------ */

const HistoryInfoDialog = ({
  open,
  onOpenChange,
  title,
  colorMode = "time",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  colorMode?: "time" | "source";
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden">
      {/* Header */}
      <DialogHeader className="px-5 py-4 border-b border-border/60">
        <DialogTitle className="z-label text-foreground">{title}</DialogTitle>
      </DialogHeader>

      <div className="px-5 py-4 space-y-3">
        {/* Unconfirmed section */}
        <div className="rounded-lg border border-border/50 bg-muted/20 overflow-hidden">
          <div className="px-3 py-2 border-b border-border/40 bg-muted/30">
            <span className="z-eyebrow text-muted-foreground">
              Unconfirmed (Self-Reported)
            </span>
          </div>
          <div className="px-3 py-2">
            <p className="z-caption text-muted-foreground mb-2">
              Patient-reported from intake. Not yet clinically reviewed.
            </p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center size-5 rounded bg-muted border border-border/60 shrink-0">
                  <Check className="size-3 text-muted-foreground" />
                </span>
                <span className="z-caption text-muted-foreground">
                  Agree — moves to Confirmed.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center size-5 rounded bg-muted border border-border/60 shrink-0">
                  <Minus className="size-3 text-muted-foreground" />
                </span>
                <span className="z-caption text-muted-foreground">
                  Dismiss — stays on record as inactive.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmed section */}
        <div className="rounded-lg border border-border/50 bg-muted/20 overflow-hidden">
          <div className="px-3 py-2 border-b border-border/40 bg-muted/30">
            <span className="z-eyebrow text-muted-foreground">
              Confirmed (Diagnosed Previously)
            </span>
          </div>
          <div className="px-3 py-2">
            <p className="z-caption text-muted-foreground mb-2">
              Clinically validated past diagnoses.
            </p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center size-5 rounded bg-muted border border-border/60 shrink-0">
                  <Plus className="size-3 text-muted-foreground" />
                </span>
                <span className="z-caption text-muted-foreground">
                  Add a past diagnosis.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="flex items-center justify-center size-5 rounded bg-muted border border-border/60 shrink-0 mt-0.5">
                  <Minus className="size-3 text-muted-foreground" />
                </span>
                <div className="z-caption text-muted-foreground space-y-0.5">
                  <p>Added this session → deleted.</p>
                  <p>Pre-existing → moved to inactive.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="z-hint text-muted-foreground/70 px-0.5">
          Inactive tags are greyed out within their section. Use{" "}
          <span className="inline-flex items-center justify-center size-4 rounded bg-muted border border-border/60 align-middle">
            <Plus className="size-2.5 text-muted-foreground" />
          </span>{" "}
          on any inactive tag to restore it.
        </p>
      </div>
    </DialogContent>
  </Dialog>
);

/* ------------------------------------------------------------------ */
/*  InactiveSubsection — greyed inactive chips with restore action     */
/* ------------------------------------------------------------------ */

// Rendered at the bottom of each Pre-Existing Conditions group so the user
// can see what was rejected/deactivated/excluded and restore it in place.
// Restore PATCHes activity_status:"active" on the draft item; there is no
// dedicated reactivate endpoint (per docs/24-apr-rupesh-frontend-disease-api-flow.md).
const InactiveSubsection = ({
  label,
  rows,
  isCompleted,
  onRestore,
  renderLabel,
}: {
  label: string;
  rows: PatientDiseaseHistory[];
  isCompleted?: boolean;
  onRestore: (row: PatientDiseaseHistory) => void;
  renderLabel: (row: PatientDiseaseHistory) => string;
}) => {
  return (
    <div className="space-y-1 pt-1">
      <p className="z-hint text-muted-foreground/70 uppercase tracking-wide">
        {label}
      </p>
      <ChipContainer>
        {rows.map((row) => (
          <Badge
            key={row.id}
            variant="secondary"
            className="h-7 px-3 z-caption font-normal gap-1.5 transition-colors rounded-full border border-border/40 bg-muted/40 text-muted-foreground line-through decoration-muted-foreground/60"
          >
            {renderLabel(row)}
            {!isCompleted && (
              <button
                type="button"
                onClick={() => onRestore(row)}
                className="rounded p-0.5 no-underline transition-colors bg-muted text-muted-foreground hover:bg-success hover:text-success"
                title="Restore"
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </ChipContainer>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  PreExistingConditionsWidget — two-section disease history          */
/* ------------------------------------------------------------------ */

interface PreExistingConditionsWidgetProps {
  patientId: string;
  appointmentId: string;
  onAddConfirmed?: () => void;
  isCompleted?: boolean;
}

const PreExistingConditionsWidget = ({
  patientId,
  appointmentId,
  onAddConfirmed,
  isCompleted,
}: PreExistingConditionsWidgetProps) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const [customReviewRow, setCustomReviewRow] =
    useState<PatientDiseaseHistory | null>(null);
  const [customMappingRow, setCustomMappingRow] =
    useState<PatientDiseaseHistory | null>(null);
  const [customRemoveRow, setCustomRemoveRow] =
    useState<PatientDiseaseHistory | null>(null);
  const { toast } = useToast();
  const { isNutritionist } = useAuthStore();
  const source: "meal_plan" | "prescription" = isNutritionist()
    ? "meal_plan"
    : "prescription";

  // The appointment draft is the single source of truth during a consultation.
  // Initialise it once per (appointment, source) so the widget can read/write
  // against AppointmentDiseaseDraftItem instead of committed PatientDisease.
  const { mutate: initializeDraft } = useInitializeAppointmentDiseaseDraft();
  const initializedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${appointmentId}:${source}`;
    if (!appointmentId || initializedRef.current === key) return;
    initializedRef.current = key;
    initializeDraft({ appointmentId, source });
  }, [appointmentId, source, initializeDraft]);

  const { data: draftItems = [] } = usePatientDiseaseHistory(
    appointmentId,
    source
  );

  const confirmMutation = useConfirmDiseaseHistory(appointmentId, patientId);
  const rejectMutation = useRejectDiseaseHistory(appointmentId, patientId);
  const rejectCustomMutation = useRejectCustomDisease(appointmentId, patientId);
  const deactivateMutation = useDeactivateDiseaseHistory(
    appointmentId,
    patientId
  );
  // Past prescription rows must be removed via exclude-from-prescription, not
  // deactivate/PATCH/DELETE (docs/frontend-disease-api-flow.md, "Move Between
  // Quadrants" + "Update Draft Item Fields > Notes"). Session-added rows are
  // deleted for both appointment sources.
  const excludeFromPrescriptionMutation =
    useExcludeFromPrescription(appointmentId);
  const includeInPrescriptionMutation = useIncludeInPrescription(appointmentId);
  const deleteMutation = useDeleteDiseaseHistory();
  const removeConfirmedRow = (row: PatientDiseaseHistory) => {
    if (!isPastDraft(row)) {
      deleteMutation.mutate({
        id: appointmentId,
        rowId: row.id,
        source,
      });
      return;
    }
    if (source === "prescription") {
      excludeFromPrescriptionMutation.mutate(row.id);
      return;
    }
    deactivateMutation.mutate(row.id);
  };
  // Restore an inactive draft item back to active. Prescription past rows must
  // use the symmetric include-in-prescription action instead of PATCH.
  const updateMutation = useUpdateDiseaseHistory();
  const restoreDraftItem = (row: PatientDiseaseHistory) => {
    if (source === "prescription" && isPastDraft(row)) {
      includeInPrescriptionMutation.mutate(row.id);
      return;
    }
    updateMutation.mutate({
      id: appointmentId,
      rowId: row.id,
      data: { activity_status: "active" },
      source,
    });
  };

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof error.message === "string"
    ) {
      return error.message;
    }
    return fallback;
  };

  const handleRemoveCustomDisease = async () => {
    if (!customRemoveRow) return;

    try {
      await rejectCustomMutation.mutateAsync(customRemoveRow.id);
      toast({
        title: "Success",
        description: "Custom disease marked inactive",
      });
      setCustomRemoveRow(null);
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(
          error,
          "Failed to mark custom disease inactive"
        ),
        variant: "destructive",
      });
    }
  };

  const {
    pendingSelfReported,
    confirmedActive,
    pendingCustom,
    inactiveSelfReported,
    inactiveConfirmed,
    inactiveCustom,
  } = useMemo(() => {
    const isActive = (h: PatientDiseaseHistory) =>
      h.activity_status !== "inactive";
    const isSelfReportedCatalog = (h: PatientDiseaseHistory) =>
      h.disease_status === "self_reported" && h.disease != null;
    const isConfirmedCatalog = (h: PatientDiseaseHistory) =>
      (isPastDraft(h) || h.draft_context === "in_progress") &&
      (h.disease_status === "confirmed" ||
        h.disease_status === "provisional") &&
      h.disease != null;
    const isCustomRow = (h: PatientDiseaseHistory) => h.disease == null;

    const activeRows = draftItems.filter(isActive);
    const inactiveRows = draftItems.filter((h) => !isActive(h));

    return {
      pendingSelfReported: activeRows.filter(isSelfReportedCatalog),
      confirmedActive: activeRows.filter(isConfirmedCatalog),
      pendingCustom: activeRows.filter(
        (h) => isCustomRow(h) && h.disease_status === "self_reported"
      ),
      // Inactive rows live next to their original quadrant so the user can see
      // exactly what was rejected/deactivated and restore it in-place. Custom
      // rows that were never resolved fall into the custom inactive bucket
      // regardless of their disease_status (reject leaves it self_reported).
      inactiveSelfReported: inactiveRows.filter(isSelfReportedCatalog),
      inactiveConfirmed: inactiveRows.filter(isConfirmedCatalog),
      inactiveCustom: inactiveRows.filter(isCustomRow),
    };
  }, [draftItems]);

  return (
    <>
      <Widget>
        <WidgetHeader
          title="Pre-Existing Conditions"
          onInfo={() => setInfoOpen(true)}
          hasInfo
        />
        <WidgetContent>
          <div className="space-y-2">
            {/* ── Unconfirmed (Self-Reported) ── */}
            <div className="rounded-xl border border-border/50 bg-muted/20 p-3 space-y-1.5">
              <p className="z-label text-muted-foreground">
                Unconfirmed (Self-Reported)
              </p>
              {pendingSelfReported.length > 0 ? (
                <ChipContainer>
                  {pendingSelfReported.map((h) => (
                    <Badge
                      key={h.id}
                      variant="secondary"
                      className="h-7 px-3 z-caption font-normal gap-1.5 transition-colors rounded-full border border-border/80 bg-card text-foreground"
                    >
                      {h.disease!.display_name}
                      {!isCompleted && (
                        <>
                          <button
                            type="button"
                            onClick={() => confirmMutation.mutate(h.id)}
                            className="rounded p-0.5 transition-colors bg-muted text-muted-foreground hover:bg-success hover:text-success"
                            title="Confirm condition"
                          >
                            <Check className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => rejectMutation.mutate(h.id)}
                            className="rounded p-0.5 transition-colors bg-muted text-muted-foreground hover:bg-destructive/20 hover:text-destructive"
                            title="Dismiss"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </Badge>
                  ))}
                </ChipContainer>
              ) : (
                <EmptyState title="None reported" variant="compact" />
              )}
              {inactiveSelfReported.length > 0 && (
                <InactiveSubsection
                  label="Inactive"
                  rows={inactiveSelfReported}
                  isCompleted={isCompleted}
                  onRestore={restoreDraftItem}
                  renderLabel={(h) => h.disease!.display_name}
                />
              )}
            </div>

            {/* ── Confirmed (Diagnosed Previously) ── */}
            <div className="rounded-xl border border-border/50 bg-muted/20 p-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="z-label text-muted-foreground">
                  Confirmed (Diagnosed Previously)
                </p>
                {!isCompleted && onAddConfirmed && (
                  <button
                    type="button"
                    className="flex items-center justify-center size-5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    onClick={onAddConfirmed}
                  >
                    <Plus className="size-3" />
                  </button>
                )}
              </div>
              {confirmedActive.length > 0 ? (
                <ChipContainer>
                  {confirmedActive.map((h) => (
                    <Badge
                      key={h.id}
                      variant="secondary"
                      className={`h-7 px-3 z-caption font-normal gap-1.5 transition-colors rounded-full border ${
                        h.disease_status === "provisional"
                          ? "border-warning/30 bg-warning/10 text-warning"
                          : "border-border/80 bg-card text-foreground"
                      }`}
                    >
                      {h.disease_status === "provisional"
                        ? `?${h.disease!.display_name}`
                        : h.disease!.display_name}
                      {!isCompleted && (
                        <button
                          type="button"
                          onClick={() => removeConfirmedRow(h)}
                          className="rounded p-0.5 transition-colors bg-muted text-muted-foreground hover:bg-destructive/20 hover:text-destructive"
                          title={
                            source === "prescription" && isPastDraft(h)
                              ? "Exclude from prescription"
                              : isPastDraft(h)
                                ? "Deactivate"
                                : "Remove"
                          }
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </ChipContainer>
              ) : (
                <EmptyState title="None confirmed" variant="compact" />
              )}
              {inactiveConfirmed.length > 0 && (
                <InactiveSubsection
                  label="Inactive"
                  rows={inactiveConfirmed}
                  isCompleted={isCompleted}
                  onRestore={restoreDraftItem}
                  renderLabel={(h) =>
                    h.disease_status === "provisional"
                      ? `?${h.disease!.display_name}`
                      : h.disease!.display_name
                  }
                />
              )}
            </div>

            {/* ── Custom (Unresolved) ── */}
            {(pendingCustom.length > 0 || inactiveCustom.length > 0) && (
              <div className="rounded-xl border border-border/50 bg-muted/20 p-3 space-y-1.5">
                <p className="z-label text-muted-foreground">
                  Custom (Unresolved)
                </p>
                {pendingCustom.length > 0 && (
                  <>
                    <p className="z-hint text-muted-foreground/80">
                      Resolve or reject during prescription.
                    </p>
                    <ChipContainer>
                      {pendingCustom.map((c) => (
                        <Badge
                          key={c.id}
                          variant="secondary"
                          className="h-7 px-3 z-caption font-normal gap-1.5 transition-colors rounded-full border border-warning/30 bg-warning/10 text-warning"
                        >
                          {c.custom_disease_name}
                          {!isCompleted && (
                            <button
                              type="button"
                              onClick={() => setCustomReviewRow(c)}
                              className="rounded p-0.5 transition-colors bg-warning/15 text-warning hover:bg-warning/25 hover:text-warning dark:bg-warning/20 dark:hover:bg-warning/30"
                              title="Review custom disease"
                            >
                              <CircleAlert className="h-3 w-3" />
                            </button>
                          )}
                        </Badge>
                      ))}
                    </ChipContainer>
                  </>
                )}
                {inactiveCustom.length > 0 && (
                  <InactiveSubsection
                    label="Inactive"
                    rows={inactiveCustom}
                    isCompleted={isCompleted}
                    onRestore={restoreDraftItem}
                    renderLabel={(c) =>
                      c.custom_disease_name ?? "Custom disease"
                    }
                  />
                )}
              </div>
            )}
          </div>
        </WidgetContent>
      </Widget>

      <HistoryInfoDialog
        open={infoOpen}
        onOpenChange={setInfoOpen}
        title="Pre-Existing Conditions"
        colorMode="source"
      />

      <Dialog
        open={customReviewRow != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setCustomReviewRow(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px] border-border/60">
          <DialogHeader>
            <DialogTitle>Resolve Custom Disease</DialogTitle>
            <DialogDescription>
              Choose an action for "
              {customReviewRow?.custom_disease_name ?? "Custom disease"}".
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Button
              type="button"
              onClick={() => {
                setCustomMappingRow(customReviewRow);
                setCustomReviewRow(null);
              }}
            >
              Map existing
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setCustomRemoveRow(customReviewRow);
                setCustomReviewRow(null);
              }}
            >
              Remove
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={customRemoveRow != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setCustomRemoveRow(null);
        }}
      >
        <DialogContent className="sm:max-w-[440px] border-border/60">
          <DialogHeader>
            <DialogTitle>Remove custom disease?</DialogTitle>
            <DialogDescription>
              "{customRemoveRow?.custom_disease_name ?? "Custom disease"}" will
              be marked inactive. It can be restored before publish.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="destructive"
              onClick={handleRemoveCustomDisease}
              disabled={rejectCustomMutation.isPending}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddDiseaseHistoryDialog
        open={customMappingRow != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setCustomMappingRow(null);
        }}
        patientId={patientId}
        appointmentId={appointmentId}
        defaultSource={source}
        customDiseaseToMap={customMappingRow}
      />
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  AllVisitsDialog — paginated full view of last visits               */
/* ------------------------------------------------------------------ */

const VISITS_PER_PAGE = 10;

const AllVisitsDialog = ({
  open,
  onOpenChange,
  visits,
  formatDate,
  onViewPrescription,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visits: AppointmentHistory[];
  formatDate: (d: string) => {
    month: string;
    day: string;
    weekday: string;
    groupKey: string;
  };
  onViewPrescription: (visit: AppointmentHistory) => void;
}) => {
  const [page, setPage] = React.useState(0);
  const totalPages = Math.ceil(visits.length / VISITS_PER_PAGE);
  const pageVisits = visits.slice(
    page * VISITS_PER_PAGE,
    (page + 1) * VISITS_PER_PAGE
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="z-h3">
            Last Visits ({visits.length})
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto scrollbar-thin space-y-1 pr-1">
          {pageVisits.map((visit) => {
            const dateInfo = formatDate(visit.start_time);
            return (
              <div
                key={visit.id}
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-border/60 shadow-sm bg-card group hover:bg-muted/40 transition-colors"
              >
                <Badge
                  variant="secondary"
                  className="shrink-0 rounded-md px-1.5 py-0.5 z-caption font-normal tabular-nums"
                >
                  {dateInfo.day} {dateInfo.month}
                </Badge>

                <div className="flex-1 min-w-0">
                  <p className="z-secondary text-foreground truncate">
                    {visit.doctor_name || "Doctor"}
                    {visit.doctor_designation
                      ? ` (${visit.doctor_designation})`
                      : ""}
                  </p>
                  <p className="z-caption text-muted-foreground truncate">
                    {visit.service_name}
                  </p>
                </div>

                {visit.prescription_url ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 gap-1 shrink-0 rounded-md shadow-none z-caption font-normal tabular-nums"
                    onClick={() => onViewPrescription(visit)}
                  >
                    {visit.zarity_appointment_id}
                    <ExternalLink className="size-2.5" />
                  </Button>
                ) : (
                  <Badge
                    variant="outline"
                    className="shrink-0 rounded-md px-1.5 z-caption font-normal tabular-nums"
                  >
                    {visit.zarity_appointment_id}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-3 border-t border-border/60">
            <span className="z-caption text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-7 w-7 p-0 rounded-md"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="size-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 w-7 p-0 rounded-md"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const PatientOverview = ({
  patientInfo,
  patientId,
  appointmentId,
  familyHistory,
  patientLifestyle,
  patientActivity,
  lifestyleTypes,
  lifestyleFrequencies,
  activityTypes,
  activityFrequencies,
  activityDurations,
  lastVisits = [],
  onAddConfirmed,
  onManageFatherHistory,
  onManageMotherHistory,
  onManageSiblingHistory,
  onRemoveFamilyDiseaseHistory,
  onManageLifestyle,
  onManageActivity,
  onOpenDocument,
  isCompleted = false,
}: PatientOverviewProps) => {
  const [allVisitsOpen, setAllVisitsOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<{
    id: number | string;
    label: string;
  } | null>(null);

  const handleViewPrescription = (visit: AppointmentHistory) => {
    if (onOpenDocument && visit.prescription_url) {
      onOpenDocument({
        id: `prescription-${visit.id}`,
        description: `Prescription - ${visit.zarity_appointment_id}`,
        file: visit.prescription_url,
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
      day: date.getDate().toString().padStart(2, "0"),
      weekday: date
        .toLocaleDateString("en-US", { weekday: "short" })
        .toUpperCase(),
      groupKey: `${date.toLocaleDateString("en-US", { month: "long" })} ${date.getFullYear()}`,
    };
  };

  const groupedVisits = React.useMemo(() => {
    const groups: { label: string; visits: typeof lastVisits }[] = [];
    let currentGroup: (typeof groups)[0] | null = null;
    for (const visit of lastVisits) {
      const { groupKey } = formatDate(visit.start_time);
      if (!currentGroup || currentGroup.label !== groupKey) {
        currentGroup = { label: groupKey, visits: [] };
        groups.push(currentGroup);
      }
      currentGroup.visits.push(visit);
    }
    return groups;
  }, [lastVisits]);

  const familyHistoryRows = [
    {
      label: "Father's",
      relation: "FATHER" as const,
      items: familyHistory.filter((h) => h.relations === "FATHER"),
      onAdd: onManageFatherHistory,
    },
    {
      label: "Mother's",
      relation: "MOTHER" as const,
      items: familyHistory.filter((h) => h.relations === "MOTHER"),
      onAdd: onManageMotherHistory,
    },
    {
      label: "Sibling's",
      relation: "SIBLING" as const,
      items: familyHistory.filter((h) => h.relations === "SIBLING"),
      onAdd: onManageSiblingHistory,
    },
  ];

  return (
    <>
      <div className="h-full overflow-y-auto scrollbar-thin p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 h-full">
          {/* Left Column */}
          <div className="flex flex-col gap-3">
            {/* Pre-Existing Conditions */}
            <PreExistingConditionsWidget
              patientId={patientId}
              appointmentId={appointmentId}
              onAddConfirmed={!isCompleted ? onAddConfirmed : undefined}
              isCompleted={isCompleted}
            />

            {/* Family History */}
            <Widget>
              <WidgetHeader title="Family History" hasInfo />
              <WidgetContent>
                <div className="rounded-lg border border-border/60 overflow-hidden">
                  <Table>
                    <TableBody>
                      {familyHistoryRows.map((row) => {
                        const hasItems = row.items.length > 0;
                        return (
                          <TableRow
                            key={row.relation}
                            className="hover:bg-transparent"
                          >
                            <TableCell className="py-2 px-3 whitespace-nowrap w-0 align-top">
                              <span className="z-eyebrow text-muted-foreground">
                                {row.label}
                              </span>
                              {hasItems && (
                                <span className="z-hint text-muted-foreground/70 ml-1">
                                  ({row.items.length})
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 px-3">
                              <div className="flex flex-wrap items-center gap-1.5">
                                {hasItems ? (
                                  row.items.map((h) => (
                                    <DismissibleChip
                                      key={h.id}
                                      label={h.disease.display_name}
                                      className="border-warning/30 bg-warning/10 text-warning"
                                      onRemove={
                                        !isCompleted &&
                                        onRemoveFamilyDiseaseHistory
                                          ? () =>
                                              setPendingRemoval({
                                                id: h.id,
                                                label: h.disease.display_name,
                                              })
                                          : undefined
                                      }
                                    />
                                  ))
                                ) : (
                                  <span className="z-caption text-muted-foreground/50">
                                    No data yet
                                  </span>
                                )}
                                {!isCompleted && (
                                  <button
                                    type="button"
                                    onClick={row.onAdd}
                                    className="flex items-center justify-center size-5 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                                  >
                                    <Plus className="size-3" />
                                  </button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </WidgetContent>
            </Widget>
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-3">
            {/* Last Visits */}
            <Widget>
              <WidgetHeader
                title="Last Visits"
                hasInfo
                onMaximize={
                  lastVisits.length > 0
                    ? () => setAllVisitsOpen(true)
                    : undefined
                }
              />
              <WidgetContent>
                {lastVisits.length > 0 ? (
                  <div className="max-h-[11rem] overflow-y-auto scrollbar-thin space-y-1">
                    {groupedVisits
                      .flatMap((group, gi) => {
                        const items: React.ReactNode[] = [];
                        if (gi > 0) {
                          items.push(
                            <div
                              key={`sep-${group.label}`}
                              className="flex items-center gap-2 py-0.5"
                            >
                              <div className="flex-1 h-px bg-border/60" />
                              <span className="z-hint text-muted-foreground">
                                {group.label}
                              </span>
                              <div className="flex-1 h-px bg-border/60" />
                            </div>
                          );
                        }
                        group.visits.forEach((visit) => {
                          const dateInfo = formatDate(visit.start_time);
                          items.push(
                            <div
                              key={visit.id}
                              className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-border/60 shadow-sm bg-card group hover:bg-muted/40 transition-colors"
                            >
                              {/* Date pill */}
                              <Badge
                                variant="secondary"
                                className="shrink-0 rounded-md px-1.5 py-0.5 z-caption font-normal tabular-nums"
                              >
                                {dateInfo.day} {dateInfo.month}
                              </Badge>

                              {/* Doctor + service */}
                              <div className="flex-1 min-w-0">
                                <p className="z-secondary text-foreground truncate group-hover:text-primary transition-colors">
                                  {visit.doctor_name || "Doctor"}
                                  {visit.doctor_designation
                                    ? ` (${visit.doctor_designation})`
                                    : ""}
                                </p>
                                <p className="z-caption text-muted-foreground truncate">
                                  {visit.service_name}
                                </p>
                              </div>

                              {/* Prescription pill button */}
                              {visit.prescription_url ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 gap-1 shrink-0 rounded-md shadow-none z-caption font-normal tabular-nums"
                                  onClick={() => handleViewPrescription(visit)}
                                >
                                  {visit.zarity_appointment_id}
                                  <ExternalLink className="size-2.5" />
                                </Button>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="shrink-0 rounded-md px-2 z-caption font-normal tabular-nums"
                                >
                                  {visit.zarity_appointment_id}
                                </Badge>
                              )}
                            </div>
                          );
                        });
                        return items;
                      })
                      .slice(0, 10)}
                  </div>
                ) : (
                  <EmptyState title="No previous visits" variant="compact" />
                )}
              </WidgetContent>
            </Widget>

            {/* All Visits Dialog */}
            <AllVisitsDialog
              open={allVisitsOpen}
              onOpenChange={setAllVisitsOpen}
              visits={lastVisits}
              formatDate={formatDate}
              onViewPrescription={handleViewPrescription}
            />

            {/* Activities */}
            <Widget>
              <WidgetHeader
                title="Activities"
                onAdd={isCompleted ? undefined : onManageActivity}
                hasInfo
              />
              <WidgetContent>
                {patientActivity.length > 0 ? (
                  <div className="flex gap-1.5 overflow-x-auto scrollbar-thin pb-1">
                    {patientActivity.map((activity) => {
                      const activityType = activityTypes.find(
                        (t) => t.id === activity.activity_type
                      );
                      const activityDuration = activityDurations.find(
                        (d) => d.id === activity.activity_duration
                      );
                      const activityFrequency = activityFrequencies.find(
                        (f) => f.id === activity.activity_frequency
                      );
                      return (
                        <div
                          key={activity.id}
                          className="flex flex-col items-center p-2.5 rounded-xl border border-border/60 bg-muted/30 hover:bg-muted/50 transition-colors shrink-0 min-w-[80px] gap-0.5"
                        >
                          <Footprints className="size-4 text-muted-foreground" />
                          <span className="z-caption text-foreground text-center">
                            {activityType?.name || "Activity"}
                          </span>
                          {activityDuration && (
                            <span className="z-hint text-muted-foreground">
                              {activityDuration.duration}
                            </span>
                          )}
                          {activityFrequency && (
                            <span className="z-hint text-muted-foreground">
                              {activityFrequency.frequency}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState title="No activity data" variant="compact" />
                )}
              </WidgetContent>
            </Widget>

            {/* Lifestyle Habits */}
            <Widget>
              <WidgetHeader
                title="Lifestyle Habits"
                onAdd={isCompleted ? undefined : onManageLifestyle}
                hasInfo
              />
              <WidgetContent>
                {patientLifestyle.length > 0 ? (
                  <div className="flex gap-1.5 overflow-x-auto scrollbar-thin pb-1">
                    {patientLifestyle.slice(0, 4).map((lifestyle) => {
                      const lifestyleType = lifestyleTypes.find(
                        (t) => t.id === lifestyle.lifestyle_type
                      );
                      const lifestyleFrequency = lifestyleFrequencies.find(
                        (f) => f.id === lifestyle.lifestyle_frequency
                      );
                      return (
                        <div
                          key={lifestyle.id}
                          className="flex flex-col items-center p-2.5 rounded-xl border border-border/60 bg-muted/30 hover:bg-muted/50 transition-colors shrink-0 min-w-[80px] gap-0.5"
                        >
                          <span className="z-caption text-foreground text-center">
                            {lifestyleType?.name || "Type"}
                          </span>
                          {lifestyleFrequency && (
                            <span className="z-hint text-muted-foreground">
                              {lifestyleFrequency.frequency}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState title="No lifestyle data" variant="compact" />
                )}
              </WidgetContent>
            </Widget>
          </div>
        </div>
      </div>

      <AlertDialog
        open={pendingRemoval !== null}
        onOpenChange={(open) => !open && setPendingRemoval(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from family history?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{pendingRemoval?.label}" from the
              patient's record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingRemoval) {
                  onRemoveFamilyDiseaseHistory?.(pendingRemoval.id);
                }
                setPendingRemoval(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
